import React from "react";
import { Button, Spin, Table, Tag, Space, Typography } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { connect } from "react-redux";

import Base from "../Base";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { getMerchantSettings } from "../../utils/redux/reducers/Merchant";
import EditDeliveryZoneForm from "./EditDeliveryZoneForm";

const { Paragraph } = Typography;

class DeliveryZoneManagement extends Base {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      data: [],
      zoneData: null,
      loading: false,
      saving: false,
    };
  }

  componentDidMount() {
    super.componentDidMount();
    this.onGetZoneList();
  }

  componentDidUpdate(prevProps) {
    const prevMerchantId = prevProps.user?.merchant?.id;
    const merchantId = this.props.user?.merchant?.id;
    if (prevMerchantId && merchantId && prevMerchantId !== merchantId) {
      this.onGetZoneList();
    }
  }

  onGetZoneList = async () => {
    const token = this.getUserToken();
    if (!token) return;

    const api = this.api.deliveryZone.list;
    try {
      this.setState({ loading: true });
      const res = await api.run({ token });
      if (!this.mounted) return;
      if (res.result) {
        this.setState({ data: Array.isArray(res.data) ? res.data : [] });
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
      this.setState({ loading: false, zoneData: null });
    }
  };

  onAddNew = () => {
    this.setState({ zoneData: {} });
  };

  onEdit = (record) => {
    this.setState({ zoneData: record });
  };

  onHandleCancel = () => {
    this.setState({ zoneData: null });
  };

  onFinishForm = async (values) => {
    const token = this.getUserToken();
    if (!token) return;

    const { zoneData } = this.state;
    let api = this.api.deliveryZone.add;
    const apiConfig = {
      token,
      data: values,
    };
    if (zoneData && zoneData.id) {
      api = this.api.deliveryZone.update;
      apiConfig.path = { id: zoneData.id };
    }

    try {
      this.setState({ saving: true });
      const res = await api.run(apiConfig);
      if (!this.mounted) return;
      if (res.result) {
        this.showMessage(res.message, Base.Message.success);
        this.onGetZoneList();
      } else {
        this.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      if (!this.mounted) return;
      this.handleApiError(api, error);
    } finally {
      if (!this.mounted) return;
      this.setState({ saving: false });
    }
  };

  onDelete = (id) => {
    this.showModal(
      this.i18n.t("deliveryZone.deleteWarning"),
      Base.Modal.confirm,
      async () => {
        const token = this.getUserToken();
        if (!token) return;
        const api = this.api.deliveryZone.delete;
        try {
          this.setState({ loading: true });
          const res = await api.run({ token, path: { id } });
          if (!this.mounted) return;
          if (res.result) {
            this.showMessage(res.message, Base.Message.success);
            this.onGetZoneList();
          } else {
            this.showMessage(res.message, Base.Message.error);
          }
        } catch (error) {
          if (!this.mounted) return;
          this.handleApiError(api, error);
        } finally {
          if (!this.mounted) return;
          this.setState({ loading: false });
        }
      }
    );
  };

  getNextLevel() {
    const { data } = this.state;
    if (!data.length) return 1;
    return Math.max(...data.map((item) => Number(item.level) || 0)) + 1;
  }

  getColumns() {
    return [
      {
        title: this.i18n.t("deliveryZone.level"),
        dataIndex: "level",
        key: "level",
        width: 90,
      },
      {
        title: this.i18n.t("deliveryZone.zhName"),
        dataIndex: "zh_name",
        key: "zh_name",
      },
      {
        title: this.i18n.t("deliveryZone.ugName"),
        dataIndex: "ug_name",
        key: "ug_name",
      },
      {
        title: this.i18n.t("deliveryZone.shipPrice"),
        key: "ship_price",
        render: (_, record) =>
          record.is_free ? (
            <Tag color="green">{this.i18n.t("deliveryZone.free")}</Tag>
          ) : (
            `¥${(Number(record.ship_price || 0) / 100).toFixed(2)}`
          ),
      },
      {
        title: this.i18n.t("deliveryZone.startingPrice"),
        key: "starting_price",
        render: (_, record) =>
          `¥${(Number(record.starting_price || 0) / 100).toFixed(2)}`,
      },
      {
        title: this.i18n.t("deliveryZone.enabled"),
        dataIndex: "enabled",
        key: "enabled",
        render: (enabled) =>
          enabled ? (
            <Tag color="blue">{this.i18n.t("deliveryZone.enabledOn")}</Tag>
          ) : (
            <Tag>{this.i18n.t("deliveryZone.enabledOff")}</Tag>
          ),
      },
      {
        title: this.i18n.t("order.listHeaderAction"),
        key: "action",
        render: (_, record) => (
          <Space>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => this.onEdit(record)}
            />
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => this.onDelete(record.id)}
            />
          </Space>
        ),
      },
    ];
  }

  render() {
    const { data, loading, zoneData, saving } = this.state;
    const merchant = this.props.user?.merchant || {};

    return (
      <div style={{ padding: 16 }}>
        <Paragraph type="secondary">
          {this.i18n.t("deliveryZone.pageHint")}
        </Paragraph>
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={this.onAddNew}>
            {this.i18n.t("deliveryZone.add")}
          </Button>
        </div>
        <Spin spinning={loading}>
          <Table
            rowKey="id"
            columns={this.getColumns()}
            dataSource={data}
            pagination={false}
          />
        </Spin>
        <EditDeliveryZoneForm
          visible={zoneData !== null}
          zoneData={zoneData}
          nextLevel={this.getNextLevel()}
          shopLongitude={merchant.longitude}
          shopLatitude={merchant.latitude}
          onCancel={this.onHandleCancel}
          onSubmit={this.onFinishForm}
          loading={saving}
        />
      </div>
    );
  }
}

export default connect((state) => ({
  user: getUserInfo(state),
  settings: getMerchantSettings(state),
}))(DeliveryZoneManagement);
